import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3,
  TrendingUp,
  Users,
  Eye,
  RefreshCw,
} from 'lucide-react';
import { analytics } from '../api';

const AnalyticsPage = () => {
  const [period, setPeriod] = useState('week');
  const [visitorStats, setVisitorStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
      setError(null);
    }
    try {
      const [visitors, summaryData] = await Promise.all([
        analytics.getVisitors(period),
        analytics.getSummary(),
      ]);
      setVisitorStats(visitors);
      setSummary(summaryData);
    } catch (err) {
      if (showLoading) {
        console.error('Failed to fetch analytics:', err);
        setError('Failed to load analytics data');
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  // Refetch every 24h to minimize API calls; use manual Refresh for latest data
  useEffect(() => {
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const interval = setInterval(() => fetchData(false), MS_PER_DAY);
    return () => clearInterval(interval);
  }, [period]);

  const StatCard = ({ icon: Icon, label, value, subValue, color = 'blue' }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#111118] rounded-2xl p-6 border border-white/5"
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl bg-${color}-500/10 flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}-400`} />
        </div>
      </div>
      <p className="text-white/60 text-sm mb-1">{label}</p>
      <p className="text-3xl font-bold text-white">{value}</p>
      {subValue && (
        <p className="text-sm text-white/40 mt-1">{subValue}</p>
      )}
    </motion.div>
  );

  const getMaxViews = () => {
    if (!visitorStats?.daily?.length) return 1;
    return Math.max(...visitorStats.daily.map(d => d.widget + d.dashboard), 1);
  };

  if (loading && !visitorStats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <p className="text-red-400 mb-4">{error}</p>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Analytics</h1>
          <p className="text-white/60">Track your widget usage and product views</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex bg-[#111118] rounded-xl p-1 border border-white/5">
            {['today', 'week', 'month'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  period === p
                    ? 'bg-blue-500 text-white'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                {p === 'today' ? 'Today' : p === 'week' ? '7 Days' : '30 Days'}
              </button>
            ))}
          </div>
          
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 rounded-lg bg-[#111118] border border-white/5 hover:bg-white/5 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 text-white/60 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Today's Visitors"
          value={summary?.today?.uniqueVisitors || 0}
          subValue="Unique IPs"
          color="blue"
        />
        <StatCard
          icon={Eye}
          label="Today's Widget Loads"
          value={summary?.today?.totalViews || 0}
          subValue="Total loads"
          color="purple"
        />
        <StatCard
          icon={TrendingUp}
          label="This Week Visitors"
          value={summary?.thisWeek?.uniqueVisitors || 0}
          subValue="Last 7 days"
          color="green"
        />
        <StatCard
          icon={BarChart3}
          label="This Week Loads"
          value={summary?.thisWeek?.totalViews || 0}
          subValue="Last 7 days"
          color="orange"
        />
      </div>

      {/* Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-[#111118] rounded-2xl p-6 border border-white/5"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Visitor Trends</h2>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-white/60">Widget</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-500" />
              <span className="text-white/60">Dashboard</span>
            </div>
          </div>
        </div>
        
        {visitorStats?.daily?.length > 0 ? (
          <div className="h-64 flex items-end gap-2">
            {visitorStats.daily.map((day, i) => {
              const maxHeight = getMaxViews();
              const widgetHeight = (day.widget / maxHeight) * 100;
              const dashboardHeight = (day.dashboard / maxHeight) * 100;
              const date = new Date(day.date);
              const label = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
              
              return (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex flex-col items-center gap-1 h-52">
                    <div className="flex-1 w-full flex items-end justify-center gap-1">
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: `${widgetHeight}%` }}
                        transition={{ delay: i * 0.05 }}
                        className="w-1/3 bg-blue-500 rounded-t-sm min-h-[4px]"
                        title={`Widget: ${day.widget}`}
                      />
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: `${dashboardHeight}%` }}
                        transition={{ delay: i * 0.05 + 0.025 }}
                        className="w-1/3 bg-purple-500 rounded-t-sm min-h-[4px]"
                        title={`Dashboard: ${day.dashboard}`}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-white/40 truncate w-full text-center">
                    {period === 'today' ? label : date.getDate()}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/40">No data for this period</p>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AnalyticsPage;
